import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Coffee, UtensilsCrossed, BookOpen, Hourglass } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const ALL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TYPES = [
  { value: "period", label: "Period", icon: BookOpen, bg: "#A7E4D3" },
  { value: "break", label: "Break", icon: Coffee, bg: "#FDE68A" },
  { value: "lunch", label: "Lunch", icon: UtensilsCrossed, bg: "#F9A8D4" },
  { value: "free", label: "Free", icon: Hourglass, bg: "#C7D2FE" },
];

export default function ScheduleBuilder() {
  const [days, setDays] = useState([]);
  const [slots, setSlots] = useState([]);
  const [weeks, setWeeks] = useState(1);
  const [overrides, setOverrides] = useState({});
  const [overrideDay, setOverrideDay] = useState(null);
  const [overrideSlots, setOverrideSlots] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/school/schedule").then((r) => {
      setDays(r.data.days);
      setSlots(r.data.slots);
      setWeeks(r.data.weeks || 1);
      setOverrides(r.data.day_overrides || {});
      setLoaded(true);
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!loaded) return <div className="font-display font-bold animate-pulse p-6" data-testid="schedule-loading">Loading schedule…</div>;

  const setSlot = (i, patch) => setSlots(slots.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/school/schedule", { days, slots, weeks, day_overrides: overrides });
      toast.success("Schedule layout saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="schedule-builder">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="nb-label">Schedule layout</span>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Shape of the school day</h1>
          <p className="text-zinc-500 mt-2 max-w-xl">These slots form the grid pupils fill in. Breaks and lunch are locked rows on every pupil timetable.</p>
        </div>
        <button onClick={save} disabled={saving || !days.length || !slots.length} data-testid="save-schedule-btn" className="nb-btn bg-[#1E3A8A] text-white px-5 py-2.5">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save layout"}
        </button>
      </div>

      <div className="mt-8 nb-card p-6">
        <h2 className="font-display font-bold text-lg">School days</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          {ALL_DAYS.map((d) => {
            const active = days.includes(d);
            return (
              <button key={d} type="button" data-testid={`day-toggle-${d.toLowerCase()}`}
                onClick={() => setDays(active ? days.filter((x) => x !== d) : ALL_DAYS.filter((x) => days.includes(x) || x === d))}
                className={`px-4 py-2 rounded-xl border-2 border-zinc-900 font-bold text-sm transition-[transform,box-shadow,background-color] duration-150 ${
                  active ? "bg-[#A7E4D3] shadow-[3px_3px_0px_0px_#18181B]" : "bg-white text-zinc-400"
                }`}>
                {d}
              </button>
            );
          })}
        </div>
        <h2 className="font-display font-bold text-lg mt-6">Week rotation</h2>
        <div className="mt-3 flex flex-wrap gap-3">
          {[[1, "Single week"], [2, "Two weeks (A / B)"]].map(([v, label]) => (
            <button key={v} type="button" data-testid={`weeks-toggle-${v}`} onClick={() => setWeeks(v)}
              className={`px-4 py-2 rounded-xl border-2 border-zinc-900 font-bold text-sm transition-[transform,box-shadow,background-color] duration-150 ${weeks === v ? "bg-[#D8B4FE] shadow-[3px_3px_0px_0px_#18181B]" : "bg-white text-zinc-400"}`}>
              {label}
            </button>
          ))}
        </div>
        {weeks === 2 && (
          <p className="text-xs text-zinc-500 font-semibold mt-2">Pupils will see Week A / Week B tabs and fill in each week separately.</p>
        )}
      </div>

      <div className="mt-6 nb-card p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-lg">Time slots</h2>
          <button type="button" data-testid="add-slot-btn"
            onClick={() => setSlots([...slots, { label: `Period ${slots.filter((s) => s.type === "period").length + 1}`, type: "period", start: "15:00", end: "16:00" }])}
            className="nb-btn bg-[#FDE68A] px-4 py-2 text-sm">
            <Plus className="w-4 h-4" /> Add slot
          </button>
        </div>
        <div className="mt-4 space-y-3">
          {slots.map((s, i) => (
            <div key={i} className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-zinc-900 p-3 bg-white" data-testid={`slot-row-${i}`}>
              <input className="nb-input flex-1 min-w-[120px]" value={s.label} data-testid={`slot-label-${i}`}
                onChange={(e) => setSlot(i, { label: e.target.value })} />
              <div className="flex rounded-xl border-2 border-zinc-900 overflow-hidden">
                {TYPES.map((t) => (
                  <button key={t.value} type="button" data-testid={`slot-type-${i}-${t.value}`}
                    onClick={() => setSlot(i, { type: t.value })}
                    className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 transition-colors duration-150 ${s.type === t.value ? "" : "bg-white text-zinc-400 hover:bg-zinc-50"}`}
                    style={s.type === t.value ? { backgroundColor: t.bg } : {}}>
                    <t.icon className="w-3.5 h-3.5" /> {t.label}
                  </button>
                ))}
              </div>
              <input type="time" className="nb-input w-[110px]" value={s.start} data-testid={`slot-start-${i}`} onChange={(e) => setSlot(i, { start: e.target.value })} />
              <span className="font-bold text-zinc-400">–</span>
              <input type="time" className="nb-input w-[110px]" value={s.end} data-testid={`slot-end-${i}`} onChange={(e) => setSlot(i, { end: e.target.value })} />
              <button type="button" data-testid={`slot-delete-${i}`} onClick={() => setSlots(slots.filter((_, j) => j !== i))}
                className="p-2 rounded-xl border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                <Trash2 className="w-4 h-4 text-red-700" />
              </button>
            </div>
          ))}
          {slots.length === 0 && <p className="text-zinc-500 text-sm py-6 text-center">No slots yet — add your first period.</p>}
        </div>
      </div>

      <div className="mt-6 nb-card p-6">
        <h2 className="font-display font-bold text-lg">Custom day layouts</h2>
        <p className="text-sm text-zinc-500 mt-1">Make one weekday different — e.g. Fridays finish at lunch with optional clubs. Applies to every week of the year. Click a day to customise its slots.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {days.map((d) => {
            const has = !!overrides[d];
            return (
              <button key={d} type="button" data-testid={`override-day-${d.toLowerCase()}`}
                onClick={() => { setOverrideDay(d); setOverrideSlots(JSON.parse(JSON.stringify(overrides[d]?.slots || slots))); }}
                className={`px-3 py-1.5 rounded-xl border-2 border-zinc-900 text-xs font-bold transition-colors duration-150 ${has ? "bg-[#D8B4FE]" : "bg-white text-zinc-500"}`}>
                {d}{has ? " ✎" : ""}
              </button>
            );
          })}
        </div>
        {overrideDay && (
          <div className="mt-4 rounded-xl border-2 border-zinc-900 p-4 bg-[#FDFBF7]" data-testid="override-editor">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="font-display font-bold">{overrideDay} layout</h3>
              <div className="flex gap-2 flex-wrap">
                <button type="button" data-testid="override-add-slot" onClick={() => setOverrideSlots([...overrideSlots, { label: "Club", type: "period", start: "12:30", end: "13:30" }])} className="nb-btn bg-white px-3 py-1.5 text-xs"><Plus className="w-3.5 h-3.5" /> Slot</button>
                {overrides[overrideDay] && (
                  <button type="button" data-testid="override-remove" onClick={() => { const next = { ...overrides }; delete next[overrideDay]; setOverrides(next); setOverrideDay(null); }} className="nb-btn bg-red-100 text-red-700 px-3 py-1.5 text-xs"><Trash2 className="w-3.5 h-3.5" /> Remove override</button>
                )}
                <button type="button" data-testid="override-save" onClick={() => { if (!overrideSlots.length) return toast.error("Add at least one slot"); setOverrides({ ...overrides, [overrideDay]: { slots: overrideSlots } }); setOverrideDay(null); toast.success(`${overrideDay} layout staged — remember to press Save layout`); }} className="nb-btn bg-[#A7E4D3] px-3 py-1.5 text-xs"><Save className="w-3.5 h-3.5" /> Keep</button>
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {overrideSlots.map((s, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`override-slot-row-${i}`}>
                  <input className="nb-input flex-1 min-w-[100px] h-9" value={s.label} data-testid={`override-slot-label-${i}`} onChange={(e) => setOverrideSlots(overrideSlots.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} />
                  <div className="flex rounded-xl border-2 border-zinc-900 overflow-hidden">
                    {TYPES.map((t) => (
                      <button key={t.value} type="button" data-testid={`override-slot-type-${i}-${t.value}`} onClick={() => setOverrideSlots(overrideSlots.map((x, j) => j === i ? { ...x, type: t.value } : x))}
                        className={`px-2 py-1.5 text-xs font-bold transition-colors duration-150 ${s.type === t.value ? "" : "bg-white text-zinc-400"}`}
                        style={s.type === t.value ? { backgroundColor: t.bg } : {}}>{t.label}</button>
                    ))}
                  </div>
                  <input type="time" className="nb-input w-[100px] h-9" value={s.start} data-testid={`override-slot-start-${i}`} onChange={(e) => setOverrideSlots(overrideSlots.map((x, j) => j === i ? { ...x, start: e.target.value } : x))} />
                  <input type="time" className="nb-input w-[100px] h-9" value={s.end} data-testid={`override-slot-end-${i}`} onChange={(e) => setOverrideSlots(overrideSlots.map((x, j) => j === i ? { ...x, end: e.target.value } : x))} />
                  <button type="button" data-testid={`override-slot-delete-${i}`} onClick={() => setOverrideSlots(overrideSlots.filter((_, j) => j !== i))} className="p-1.5 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200"><Trash2 className="w-3.5 h-3.5 text-red-700" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 nb-card p-6 overflow-x-auto">
        <h2 className="font-display font-bold text-lg mb-4">Preview</h2>
        <table className="w-full border-collapse min-w-[640px]" data-testid="schedule-preview">
          <thead>
            <tr>
              <th className="border-2 border-zinc-900 bg-zinc-900 text-white text-xs font-bold p-2 text-left w-32">Time</th>
              {days.map((d) => <th key={d} className="border-2 border-zinc-900 bg-zinc-900 text-white text-xs font-bold p-2">{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.max(slots.length, ...days.map((d) => (overrides[d]?.slots || slots).length)) }).map((_, i) => {
              const s = slots[i];
              const t = s ? TYPES.find((x) => x.value === s.type) : null;
              return (
                <tr key={i}>
                  <td className="border-2 border-zinc-900 p-2 text-xs font-bold" style={{ backgroundColor: t ? t.bg : "#E4E4E7" }}>
                    {s ? s.label : "—"}<br /><span className="text-zinc-600 font-semibold">{s ? `${s.start}–${s.end}` : ""}</span>
                  </td>
                  {days.map((d) => {
                    const ds = (overrides[d]?.slots || slots)[i];
                    if (!ds) return <td key={d} className="border-2 border-zinc-900 p-2 bg-zinc-100" />;
                    const dt = TYPES.find((x) => x.value === ds.type);
                    return (
                      <td key={d} className="border-2 border-zinc-900 p-2 text-center text-xs font-semibold text-zinc-400"
                        style={ds.type !== "period" ? { backgroundColor: dt.bg + "66" } : {}}>
                        {ds.type !== "period" ? ds.label : overrides[d] ? ds.label : "·"}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
